import { FC, LabelHTMLAttributes, PropsWithChildren, useEffect } from 'react';
import {
  FieldValues,
  FormProvider,
  useForm,
  UseFormProps,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form as ParagonForm } from '@openedx/paragon';
import { FormInput } from './form-input';
import { FormSelect } from './form-select';
import { FormProps } from './types';

export const Form = <TFieldValues extends FieldValues = FieldValues>({
  mode,
  onSubmit,
  children,
  shouldResetForm,
  defaultValues,
  validationSchema,
  options,
  ...props
}: FormProps<TFieldValues>) => {
  const formProps = useForm<TFieldValues>({
    defaultValues,
    mode,
    reValidateMode: shouldResetForm ? 'onSubmit' : 'onChange',
    resolver: yupResolver(
      validationSchema,
    ) as unknown as UseFormProps<TFieldValues>['resolver'],
    ...options,
  });

  const {
    clearErrors,
    formState: { isDirty },
  } = formProps;

  useEffect(() => {
    if (shouldResetForm) {
      requestAnimationFrame(() => {
        clearErrors();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, shouldResetForm]);

  return (
    <FormProvider {...formProps}>
      <ParagonForm onSubmit={formProps.handleSubmit(onSubmit)} {...props}>
        {children}
      </ParagonForm>
    </FormProvider>
  );
};

Form.Row = (
  ParagonForm as FC & {
    Row: FC<PropsWithChildren>;
  }
).Row;
Form.Label = (
  ParagonForm as FC & {
    Label: FC<PropsWithChildren<LabelHTMLAttributes<HTMLLabelElement>>>;
  }
).Label;
Form.Input = FormInput;
Form.Select = FormSelect;
